import { Box, ButtonBase, Switch, Tooltip, Typography } from '@mui/material';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { ChevronDown } from 'lucide-react';
import { type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SUMMARY_SEPARATOR } from '../useToolOptionsSummary';
import { type ModifierCount } from './useActiveModifierCount';

interface ConstraintSectionProps {
    /** i18n id of the modifier's name. */
    labelId: string;
    /** i18n id of the sentence that explains the modifier. */
    tooltipId: string;
    /** What the modifier is set to, read without opening the section. */
    summary: string;
    /** Whether the modifier applies. */
    on: boolean;
    /** Holds the switch where the modifier cannot apply. */
    switchDisabled?: boolean;
    onToggle: (on: boolean) => void;
    /** Shown before the switch, for a modifier that is on but does nothing. */
    warning?: ReactNode;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    /** The first section draws no rule above it. */
    first?: boolean;
    /** The modifier's settings, dimmed while it is off and held by the caller. */
    children: ReactNode;
}

/**
 * One modifier in the modifiers panel: a header row with the modifier's
 * name, its summary and its switch, over the settings it opens to. The
 * header opens and closes the settings; the switch only turns the modifier
 * on and off.
 */
export function ConstraintSection({
    labelId,
    tooltipId,
    summary,
    on,
    switchDisabled = false,
    onToggle,
    warning,
    expanded,
    onExpandedChange,
    first = false,
    children,
}: ConstraintSectionProps) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: labelId });
    return (
        <Box
            sx={
                first
                    ? undefined
                    : { borderTop: '1px solid', borderColor: 'divider' }
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pr: 1.5,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <ButtonBase
                    onClick={() => onExpandedChange(!expanded)}
                    aria-expanded={expanded}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        height: 36,
                        pl: 1,
                        gap: 1,
                        justifyContent: 'flex-start',
                    }}
                >
                    <Box
                        component="span"
                        sx={(theme) => ({
                            display: 'flex',
                            justifyContent: 'center',
                            width: 16,
                            flexShrink: 0,
                            color: 'text.muted',
                            transform: expanded ? 'none' : 'rotate(-90deg)',
                            transition: theme.transitions.create('transform', {
                                duration: 150,
                            }),
                        })}
                    >
                        <ChevronDown size={14} />
                    </Box>
                    <Tooltip
                        title={intl.formatMessage({ id: tooltipId })}
                        placement="left"
                        // The sentence describes the modifier; its name
                        // stays the label.
                        describeChild
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                flexShrink: 0,
                            }}
                        >
                            {label}
                        </Typography>
                    </Tooltip>
                    <Typography
                        variant="monoMeta"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            textAlign: 'right',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: on ? 'text.secondary' : 'text.muted',
                        }}
                    >
                        {summary}
                    </Typography>
                </ButtonBase>
                {warning}
                <Switch
                    checked={on}
                    disabled={switchDisabled}
                    inputProps={{ 'aria-label': label }}
                    onChange={(_, checked) => onToggle(checked)}
                />
            </Box>
            {expanded && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        pl: 4,
                        pr: 1.5,
                        pb: 1.5,
                        opacity: on ? 1 : 0.45,
                    }}
                >
                    {children}
                </Box>
            )}
        </Box>
    );
}

/**
 * Amber warning marker whose tooltip (an i18n message id) explains why a
 * constraint is inactive or unavailable. Kept at full opacity so it stays
 * prominent even when the surrounding row is dimmed.
 */
export function ConstraintWarningIcon({
    titleId,
    placement = 'top',
}: {
    titleId: string;
    placement?: 'top' | 'right' | 'bottom' | 'left';
}) {
    const intl = useIntl();
    return (
        <Tooltip
            title={intl.formatMessage({ id: titleId })}
            placement={placement}
        >
            <WarningAmberIcon
                sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }}
            />
        </Tooltip>
    );
}

/**
 * Reads a {@link ModifierCount} as `2 active / 1 inactive`: how many
 * modifiers apply, then, in the warning look behind a warning mark, how many
 * are on but apply to nothing. With `readsNone`, `None` stands in for no
 * modifier that applies; otherwise a zero count is left out.
 */
export function ModifierCountText({
    count,
    readsNone = false,
}: {
    count: ModifierCount;
    readsNone?: boolean;
}) {
    const readsActive = count.active > 0 || readsNone;
    return (
        <>
            {readsActive &&
                (count.active > 0 ? (
                    <FormattedMessage
                        id="constraintsActiveCount"
                        values={{ count: count.active }}
                    />
                ) : (
                    <FormattedMessage id="constraintsNoneActive" />
                ))}
            {count.inactive > 0 && (
                <>
                    {readsActive && SUMMARY_SEPARATOR}
                    <Box
                        component="span"
                        sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}
                    >
                        <WarningAmberIcon
                            sx={{
                                fontSize: 12,
                                mr: 0.5,
                                verticalAlign: 'middle',
                            }}
                        />
                        <FormattedMessage
                            id="constraintsInactiveCount"
                            values={{ count: count.inactive }}
                        />
                    </Box>
                </>
            )}
        </>
    );
}
